import { useMemo, useState } from 'react'
import { useT } from '../../hooks/useTranslation'

// ── Лёгкий markdown-рендерер для инструкции (без npm-зависимостей) ──────
// Покрывает ровно синтаксис, используемый в TG-BOT/INSTRUCTION.md:
//   заголовки #..####, `---`, блок-цитаты `>`, таблицы, маркированные и
//   нумерованные списки, абзацы;
//   инлайн: **жирный**, *курсив*, `` `код` ``, [ссылка](url), ![alt](src).
// Картинки с относительным путём (img/01-main.png) превращаются в
//   <baseUrl>/img/01-main.png — их отдаёт API /api/instruction/img/...
// Не используется там, где есть библиотека: только для этого документа.

/** Относительный src картинки → абсолютный URL (baseUrl склеивается один раз). */
function resolveUrl(src, baseUrl) {
  if (!src) return ''
  if (/^(https?:|data:|blob:|\/)/i.test(src)) return src
  return `${String(baseUrl).replace(/\/+$/, '')}/${src}`
}

// ── Якоря внутри документа (2026-09-27, task.txt) ───────────────────────
// Владелец просил в начале инструкции ссылки на основные объяснения. Раньше
// такую ссылку сделать было нельзя: заголовкам id не ставился вовсе, а каждая
// ссылка рендерилась как <a target="_blank"> — то есть [текст](#раздел) открыл
// бы ПУСТУЮ вкладку. Теперь заголовки получают якорь, а ссылки вида #... ведут
// к нужному месту инструкции (см. handleClick ниже).
//
// Правило якоря простое: заголовок → нижний регистр, всё кроме букв и цифр →
// дефис. Пример: «## 2. С чего начать» → «2-с-чего-начать». Ссылку можно писать
// не целиком, а коротким куском заголовка — «#с-чего-начать» найдёт его же
// (ищем по вхождению, первое совпадение сверху). Не нашли — не происходит
// ничего, пустая вкладка не открывается. Проверку «нет ли битых ссылок в
// INSTRUCTION.md» делает отдельный скрипт — она гоняет файл через эту же
// функцию mdAnchor.
export function mdAnchor(text) {
  return String(text || '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // ![alt](src) → alt
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')  // [текст](url) → текст
    .replace(/[*`_]/g, '')                    // **жирный**, *курсив*, `код`
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')         // эмодзи/пунктуация/пробелы → '-'
    .replace(/^-+|-+$/g, '')
}

/** Заголовок внутри контейнера по якорю ссылки: сначала точное совпадение,
 * иначе — первый, в чей якорь входит написанное. null — цели нет. */
export function findAnchorTarget(root, target) {
  if (!root || !target) return null
  const want = mdAnchor(String(target).replace(/^#/, ''))
  if (!want) return null
  let loose = null
  for (const node of root.querySelectorAll('[data-md-anchor]')) {
    const have = node.getAttribute('data-md-anchor')
    if (have === want) return node
    if (!loose && have.includes(want)) loose = node
  }
  return loose
}

/** Картинка инструкции с «мягким» падением: файл не на сервере — вместо
 * сломанной иконки рисуем аккуратное место с подписью, чтобы инструкция
 * не выглядела битой до того, как владелец добавит скриншот. */
function ImgWithFallback({ src, alt, style }) {
  const [failed, setFailed] = useState(false)
  const t = useT()
  if (failed) {
    return (
      <div style={{
        ...style,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, minHeight: 120,
        background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.14)',
        fontSize: 11.5, color: '#636366', textAlign: 'center', padding: '14px 10px',
      }}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>🖼️</div>
        <div style={{ fontSize: 12, color: '#8E8E93', fontWeight: 600 }}>{alt || t.imgScreenshot}</div>
        <div style={{ fontSize: 10.5 }}>{t.imgNotUploaded}</div>
      </div>
    )
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} style={style} />
}

/** Инлайн-разбор строки в React-ноды (bold/italic/code/link/image/текст).
 * onApiKeysGuide — колбэк спец-ссылки [текст](#api-keys): вместо обычной
 * ссылки рендерится кнопка «Как получить ключи на бирже?», открывающая гайд. */
function renderInline(text, baseUrl, keyPrefix, onApiKeysGuide) {
  const out = []
  let rest = text
  let i = 0
  const push = node => { out.push(node); i++ }

  while (rest.length) {
    let m
    if ((m = /^!\[([^\]]*)\]\(([^)]+)\)/.exec(rest))) {
      push(<ImgWithFallback key={`${keyPrefix}-img${i}`} src={resolveUrl(m[2], baseUrl)} alt={m[1]} />)
      rest = rest.slice(m[0].length)
    } else if ((m = /^\[([^\]]+)\]\(([^)]+)\)/.exec(rest))) {
      if (m[2] === '#api-keys' && onApiKeysGuide) {
        // Кнопка-надпись в инструкции: не отвлекает знающего юзера (короткий
        // текст), но открывает подробный гайд одним нажатием.
        push(
          <button
            key={`${keyPrefix}-k${i}`}
            onClick={onApiKeysGuide}
            style={{
              display: 'inline-block', margin: '4px 0', padding: '11px 18px', borderRadius: 12,
              background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)',
              color: '#34D399', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
              lineHeight: 1.4, WebkitTapHighlightColor: 'transparent',
            }}
          >
            {m[1]}
          </button>,
        )
      } else if (m[2].startsWith('#')) {
        // Ссылка ВНУТРИ документа (содержание в начале инструкции). Без
        // target="_blank" — переход делает общий обработчик в корне (handleClick
        // в Markdown), адрес в браузер не уходит. Цели нет — ничего не делаем.
        push(
          <a key={`${keyPrefix}-a${i}`} href={m[2]} data-md-inpage={m[2].slice(1)}
            style={{ color: '#34D399', textDecoration: 'none', cursor: 'pointer' }}>
            {m[1]}
          </a>,
        )
      } else {
        push(
          <a key={`${keyPrefix}-a${i}`} href={m[2]} target="_blank" rel="noreferrer"
            style={{ color: '#34D399', textDecoration: 'none' }}>
            {m[1]}
          </a>,
        )
      }
      rest = rest.slice(m[0].length)
    } else if ((m = /^\*\*([^*]+)\*\*/.exec(rest))) {
      push(<strong key={`${keyPrefix}-b${i}`} style={{ fontWeight: 700, color: '#fff' }}>{m[1]}</strong>)
      rest = rest.slice(m[0].length)
    } else if ((m = /^\*([^*]+)\*/.exec(rest))) {
      push(<em key={`${keyPrefix}-i${i}`} style={{ fontStyle: 'italic' }}>{m[1]}</em>)
      rest = rest.slice(m[0].length)
    } else if ((m = /^`([^`]+)`/.exec(rest))) {
      push(
        <code key={`${keyPrefix}-c${i}`} style={{
          background: '#1A1A1A', padding: '1px 6px', borderRadius: 6,
          fontSize: '0.9em', color: '#FFB3A0',
        }}>
          {m[1]}
        </code>,
      )
      rest = rest.slice(m[0].length)
    } else if ((m = /^[^!*`[]+/.exec(rest))) {
      out.push(m[0])
      rest = rest.slice(m[0].length)
    } else {
      out.push(rest[0])
      rest = rest.slice(1)
    }
  }
  return out
}

/** Строка таблицы `| a | b |` → ['a', 'b'] */
function splitRow(line) {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
}

/** Сепаратор таблицы: `|---|---|` (только - : | и пробелы, есть хотя бы один `-`). */
function isTableSep(line) {
  const t = line.trim()
  return t.includes('-') && t.replace(/[|\s:-]/g, '') === ''
}

/** Строки markdown → массив блоков { type, ... } */
function parseBlocks(text) {
  const lines = String(text || '').split('\n')
  const blocks = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (/^```/.test(trimmed)) {
      let code = []
      i++
      while (i < lines.length && !/^```/.test(lines[i].trim())) { code.push(lines[i]); i++ }
      i++ // пропускаем закрывающий fence
      blocks.push({ type: 'code', text: code.join('\n') })
      continue
    }

    if (!trimmed) { i++; continue }

    const h = /^(#{1,4})\s+(.*)$/.exec(trimmed)
    if (h) { blocks.push({ type: 'h', level: h[1].length, text: h[2] }); i++; continue }

    if (/^(-{3,}|\*{3,})$/.test(trimmed)) { blocks.push({ type: 'hr' }); i++; continue }

    if (trimmed.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const header = splitRow(trimmed)
      i++ // сепаратор
      const rows = []
      while (i < lines.length && lines[i].trim().includes('|')) { rows.push(splitRow(lines[i].trim())); i++ }
      blocks.push({ type: 'table', header, rows })
      continue
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines = []
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''))
        i++
      }
      blocks.push({ type: 'quote', text: quoteLines.join('\n') })
      continue
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = []
      while (i < lines.length) {
        const m = /^[-*]\s+(.*)$/.exec(lines[i].trim())
        if (!m) break
        items.push(m[1])
        i++
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    if (/^\d+[.)]\s+/.test(trimmed)) {
      const items = []
      while (i < lines.length) {
        const m = /^\d+[.)]\s+(.*)$/.exec(lines[i].trim())
        if (!m) break
        items.push(m[1])
        i++
      }
      blocks.push({ type: 'ol', items })
      continue
    }

    // Параграф: собираем подряд идущие непустые строки
    const para = []
    while (i < lines.length && lines[i].trim()) { para.push(lines[i].trim()); i++ }
    blocks.push({ type: 'p', text: para.join('\n') })
  }

  return blocks
}

const HEADING_FONT = { 1: 24, 2: 20, 3: 17, 4: 15 }

export default function Markdown({ text, baseUrl = '', onApiKeysGuide }) {
  const blocks = useMemo(() => parseBlocks(text), [text])

  // Якоря заголовков — разом на весь документ, чтобы они были уникальными
  // (одинаковые заголовки получают -2, -3…). Считаются по тому же mdAnchor,
  // которым потом ищется цель ссылки.
  const anchors = useMemo(() => {
    const seen = {}
    return blocks.map(b => {
      if (b.type !== 'h') return null
      const base = mdAnchor(b.text) || 'section'
      seen[base] = (seen[base] || 0) + 1
      return seen[base] === 1 ? base : `${base}-${seen[base]}`
    })
  }, [blocks])

  // Один обработчик на весь документ вместо onClick на каждой ссылке.
  const handleClick = e => {
    const link = e.target?.closest?.('a[data-md-inpage]')
    if (!link) return
    e.preventDefault()
    const root = link.closest('[data-md-root]')
    const target = findAnchorTarget(root, link.getAttribute('data-md-inpage'))
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const renderBlock = (b, idx) => {
    switch (b.type) {
      case 'h':
        return (
          <div key={idx} id={anchors[idx]} data-md-anchor={anchors[idx]} style={{
            fontSize: HEADING_FONT[b.level] ?? 17,
            fontWeight: 700, color: '#fff', lineHeight: 1.3,
            margin: `${b.level <= 2 ? 24 : 18}px 0 10px`,
            scrollMarginTop: 8,
          }}>
            {renderInline(b.text, baseUrl, `h${idx}`, onApiKeysGuide)}
          </div>
        )
      case 'hr':
        return <div key={idx} style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '20px 0' }} />
      case 'quote':
        return (
          <div key={idx} style={{
            borderLeft: '3px solid #34D399', background: 'rgba(16,185,129,0.06)',
            borderRadius: 8, padding: '10px 14px', margin: '12px 0',
            fontSize: 13.5, color: '#B0B0B5', lineHeight: 1.6,
          }}>
            {renderInline(b.text, baseUrl, `q${idx}`, onApiKeysGuide)}
          </div>
        )
      case 'code':
        return (
          <pre key={idx} style={{
            background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 12, padding: 12, overflowX: 'auto',
            fontSize: 12.5, lineHeight: 1.5, color: '#D0D0D5',
          }}>{b.text}</pre>
        )
      case 'ul':
        return (
          <ul key={idx} style={{ margin: '10px 0', paddingLeft: 18 }}>
            {b.items.map((it, j) => (
              <li key={j} style={{ marginBottom: 6, fontSize: 14, color: '#D0D0D5', lineHeight: 1.55 }}>
                {renderInline(it, baseUrl, `u${idx}-${j}`, onApiKeysGuide)}
              </li>
            ))}
          </ul>
        )
      case 'ol':
        return (
          <ol key={idx} style={{ margin: '10px 0', paddingLeft: 22 }}>
            {b.items.map((it, j) => (
              <li key={j} style={{ marginBottom: 6, fontSize: 14, color: '#D0D0D5', lineHeight: 1.55 }}>
                {renderInline(it, baseUrl, `o${idx}-${j}`, onApiKeysGuide)}
              </li>
            ))}
          </ol>
        )
      case 'table':
        return (
          <div key={idx} style={{ overflowX: 'auto', margin: '14px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#D0D0D5' }}>
              <thead>
                <tr>
                  {b.header.map((c, j) => (
                    <th key={j} style={{
                      textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#fff',
                      background: 'rgba(255,255,255,0.04)',
                      borderBottom: '1px solid rgba(255,255,255,0.12)',
                    }}>{renderInline(c, baseUrl, `th${idx}-${j}`, onApiKeysGuide)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((c, j) => (
                      <td key={j} style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', lineHeight: 1.5, verticalAlign: 'top' }}>
                        {renderInline(c, baseUrl, `td${idx}-${r}-${j}`, onApiKeysGuide)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      case 'p': {
        // Параграф из одной картинки → центрированная картинка с подписью
        const imgOnly = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(b.text.trim())
        if (imgOnly) {
          return (
            <figure key={idx} style={{ margin: '16px 0', textAlign: 'center' }}>
              <ImgWithFallback
                src={resolveUrl(imgOnly[2], baseUrl)}
                alt={imgOnly[1]}
                style={{
                  width: '100%', maxWidth: 420, display: 'block', margin: '0 auto',
                  borderRadius: 14, border: '1px solid rgba(255,255,255,0.08)',
                }}
              />
              <figcaption style={{ fontSize: 11.5, color: '#636366', marginTop: 6 }}>{imgOnly[1]}</figcaption>
            </figure>
          )
        }
        return (
          <p key={idx} style={{
            margin: '12px 0', fontSize: 14.5, color: '#D0D0D5',
            lineHeight: 1.65, whiteSpace: 'pre-line',
          }}>
            {renderInline(b.text, baseUrl, `p${idx}`, onApiKeysGuide)}
          </p>
        )
      }
      default:
        return null
    }
  }

  return <div data-md-root onClick={handleClick}>{blocks.map(renderBlock)}</div>
}
